import { ChangeDetectorRef, Injectable } from '@angular/core';
import { CalculationProgressModel } from '../models/calculationProgressModel';
import { Observable, firstValueFrom } from 'rxjs';
import { EventSourceMessage, EventStreamContentType, FetchEventSourceInit, fetchEventSource } from '@microsoft/fetch-event-source';
import { EndpointsService } from './endpoints.service';
import { ModalControlService } from './modal-control.service';
import { HttpClient } from '@angular/common/http';
import { CalculationsService } from './calculations.service';
import { CalculationModel } from '../models/calculations';
import { CalcConfigInfo } from '../models/calcConfigInfo';
import { PerformanceAnalysisDTO } from '../models/performanceAnalysisDTO';
import { PointsService } from './points.service';
import { ProjectService } from './project.service';
import { UserSettingsService } from './userSettings.service';
import { CalculationsControlService } from './calculations-control.service';
import { PendingCalculation, PendingCalculationDTO, PendingCalculationMap, PendingCalculationType } from '../models/pendingCalculation';
import { CalculationValidation } from './calculation-validation.service';
import { DocSectorizationDisplayService } from './doc-sectorization-display.service';
import { SitingToolService } from './siting-tool-service.service';
import { UiBlockService } from './ui-block.service';
import { Geometry } from 'ol/geom';
import { Feature } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import { ConfigurationService } from './configuration.service';
import { ConfigurationTerrain } from '../models/terrains';
import * as JSZip from 'jszip';
import { ErrorLogFile } from '../models/errorLog';
import { UtilsService } from './utils.service';
import * as fileSaver from 'file-saver';
import { ErrorLogsDisplayComponent } from '../popup/error-logs-display/error-logs-display.component';
import { CalculationsDatabaseAndUserSpaceExceeded, CalculationsDatabaseAndUserSpaceLimitWarningMessage, CalculationsDatabaseExceeded, CalculationsDatabaseLimitWarningMessage, UserSpaceExceeded, UserSpaceLimitWarningMessage } from '../models/error';
import { Project } from '../models/project';
import { Processingtool } from '../models/processingTool';

@Injectable({
  providedIn: 'root'
})
export class ProgressService {

  public progressMap: Map<string, CalculationProgressModel> = new Map<string, CalculationProgressModel>(); // one entry for each ongoing calculation

  public progressReceiverGuid: string; // current UUID for progress event socket receiver

  public cdRefCalcResultsMenu: ChangeDetectorRef;

  public previouslyFetchedCalculations: number[] = [];

  private observable: Observable<any>;

  // populated in tabs service constructor
  openNewPerformanceAnalysisTabFunction : Function;
  openNewVerticalProfileTabFunction: Function;
  openNewPerformanceReportTabFunction : Function;
  openNewCoveraveQuantitativeAssessmentTab: Function;

  // populated in horizon tool service constructor
  performHorizonTool: Function;
  horizonToolLoading: boolean = false;

  constructor(
    private http: HttpClient,
    private endpointsService: EndpointsService,
    private modalService: ModalControlService,
    private calculationsService: CalculationsService,
    private pointsService: PointsService,
    private projectService: ProjectService,
    private userSettingsService: UserSettingsService,
    private calculationsControlService: CalculationsControlService,
    private calculationValidationService: CalculationValidation,
    private docSectorizationDisplayService:DocSectorizationDisplayService,
    private sitingToolService: SitingToolService,
    private uiBlock: UiBlockService,
    private configurationService: ConfigurationService,
    private utilsService: UtilsService
    ) { }


  public async initializeProgressService(extent?: [number, number, number, number]){
    await this.getPreviousCalculationsAsync(extent);
    const pendingCalculations: PendingCalculationMap[] = await this.getAllPendingCalculations();
    this.listenForCalculationProgress();
    for (const pendingCalculation of pendingCalculations){
      const calculation: CalculationModel = await this.calculationsService.getCalculationById(pendingCalculation.calculationId);
      CalculationsService.decompressCalculationResult(calculation);
      this.mapTerrainHeightToNextCalculation(calculation, pendingCalculation);
    }
  }


  extractTemporaryCalculations(calculations: CalculationModel[]): CalculationModel[] {

    const extractedCalculations = calculations.filter(c => this.isTemporaryCalculation(c));
    const remainingCalculations = calculations.filter(c => !this.isTemporaryCalculation(c));

    // Update the original list with calculations that are not temporary
    calculations.length = 0;
    Array.prototype.push.apply(calculations, remainingCalculations);

    return extractedCalculations;
  }

  isTemporaryCalculation(calculation: CalculationModel): boolean {
    return calculation.calcType == "VERTICAL_PROFILE" || calculation.calcType == "ROUTE_PERFORMANCE"
            || calculation.calcType == "COVERAGE_QUANTITATIVE_ASSESSMENT" || calculation.calcType == "HORIZON_TOOL";
  }

  async getPreviousCalculationsAsync(extent?: [number, number, number, number], isSwitchProject: boolean = false) {
    isSwitchProject ? this.uiBlock.startBlock("Adding entities to map...") : false;
    try {
      let calculations: CalculationModel[];
      if(extent == null){
        calculations = await this.calculationsService.getPreviousCalculationsHttpRequest().toPromise();

      }
      else{
        calculations = await this.calculationsService.getPreviousPartialCalculationsHttpRequest(extent).toPromise();
      }
      if (calculations != null && calculations.length > 0){
        this.previouslyFetchedCalculations.push(...calculations.map(r => r.id));
        const temporaryCalculations: CalculationModel[] = this.extractTemporaryCalculations(calculations);
        this.calculationsService.addCalculationsToMap(calculations);
        for (const temporaryCalculation of temporaryCalculations){
          CalculationsService.decompressCalculationResult(temporaryCalculation);
          this.handleTemporaryCalculation(temporaryCalculation, true);
        }
      }
    } finally {
      isSwitchProject ? this.uiBlock.stopBlock() : false;
    }
  }

  public async listenForCalculationProgress(): Promise<void> {
    if (this.observable != null){
      return;
    }
    this.progressReceiverGuid = null;
    this.progressMap.clear();
    // Server side Events HTTP request implementation
    this.observable = new Observable(observer => {
      let abortController: AbortController | null = null; // Declare abortController variable

      const startConnection = () => {
        // Create a new AbortController instance for each connection attempt
        abortController = new AbortController();

        const eventSourceOptions : FetchEventSourceInit = {
          headers: this.endpointsService.httpOptionsAsRecordStrings,
          openWhenHidden: true,
          signal: abortController.signal,
          async onopen(response) {
            if (response.ok && response.headers.get('content-type') === EventStreamContentType) {
                return; // everything's good
            } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                // client-side errors are usually non-retriable:
                throw new FatalError();
            } else {
                throw new RetriableError();
            }
          },
          onmessage(msg) {
            // if the server emits an error message, throw an exception
            // so it gets handled by the onerror callback below:
            if (msg.event === 'FatalError') {
                throw new FatalError(msg.data);
            } else {
              observer.next(msg);
            }
          },
          onerror(err) {
            if (abortController) {
              // Abort the connection on error
              abortController.abort();
              abortController = null; // Reset abortController
            }
            if (err instanceof FatalError) {
                observer.error(err.message);
                throw err; // rethrow to stop the operation
            } else {
                // automatically retry.
                const msg: EventSourceMessage = {id: "", event: "Reconnect", data: "Lost connection to server. Reconnecting..."};
                observer.next(msg);
                return 3000; // wait time to reconnect in ms
            }
          },
          onclose(){
            observer.complete();
          }
        };

        fetchEventSource(this.endpointsService.progressCalculationUrl, eventSourceOptions);
      };

      startConnection(); // Start the initial connection

      // Cleanup function to abort the connection
      return () => {
        if (abortController) {
          abortController.abort(); // Abort the connection on cleanup
          abortController = null; // Reset abortController
        }
      };
    });

    // Progress functionality implementation
    this.observable.subscribe({
        next: (messageFromServer: EventSourceMessage) => {
          if (messageFromServer.event == "Reconnect"){
            for (const progressObj of this.progressMap.values()){
              progressObj.message = "Lost connection to server. Reconnecting...";
            }
          }
          else if (messageFromServer.id){ // guid from specific calculation
            const progressObjectFromServer: CalculationProgressModel = JSON.parse(messageFromServer.data);
            let project: Project = null;
            let projectDeleted: boolean = false;
            if (progressObjectFromServer.projectId != null){
              project = this.projectService.projects.find(p => p.id == progressObjectFromServer.projectId);
              if (project == null){
                projectDeleted = true;
                this.progressMap.has(messageFromServer.id)? this.progressMap.delete(messageFromServer.id): false;
              } else {
                progressObjectFromServer.projectName = project.name;
              }
            }
            if (!projectDeleted){
              if (this.progressMap.has(messageFromServer.id)){
                // update ongoing calculation progress parameteres
                const onGoingCalc: CalculationProgressModel = this.progressMap.get(messageFromServer.id);
                onGoingCalc.message = progressObjectFromServer.message;
                progressObjectFromServer.loadedPercentage? onGoingCalc.loadedPercentage = progressObjectFromServer.loadedPercentage: false;
                this.cdRefCalcResultsMenu.detectChanges();
              } else {
                // create new ongoing calculation progress
                this.progressMap.set(messageFromServer.id, progressObjectFromServer);
              }
              if (this.isLastMessage(progressObjectFromServer.message)) {
                this.showWarningMessage(progressObjectFromServer.message);
                this.finishProgressOnCalculation(messageFromServer.id, progressObjectFromServer);
              }
            }
          } else {
            throw new FatalError();
          }
        },
        error: (err: string) => {
          console.error('Error on progress calculation: ' + err);
        },
        complete: () => {}
    });
  }

  private isLastMessage(message: string): boolean {
    if (!message) {
      return false;
    }

    return message == CalculationProgressModel.COMPLETED_CALCULATION
      || message == UserSpaceExceeded
      || message == CalculationsDatabaseExceeded
      || message == CalculationsDatabaseAndUserSpaceLimitWarningMessage
      || message == CalculationProgressModel.CANCEL_CALCULATION;
  }


  private showWarningMessage(message: string) {
    switch (message) {
      case UserSpaceExceeded:
        this.modalService.showMesssageModal("Warning", UserSpaceLimitWarningMessage);
        break;
      case CalculationsDatabaseExceeded:
        this.modalService.showMesssageModal("Warning", CalculationsDatabaseLimitWarningMessage);
        break;
      case CalculationsDatabaseAndUserSpaceExceeded:
        this.modalService.showMesssageModal("Warning", CalculationsDatabaseAndUserSpaceLimitWarningMessage);
        break;
      default:
      // do nothing
    }
  }

  async finishProgressOnCalculation(progressGuid: string, progressObjectFromServer: CalculationProgressModel){
    try {
      if (progressObjectFromServer.loadedPercentage == CalculationProgressModel.ERROR_FLAG){ // error on progress display
        this.modalService.showMesssageModal(
          `Progress display error. Calculation: ${progressObjectFromServer.calculationName}
          ${progressObjectFromServer.projectName != null? ", Project: " + progressObjectFromServer.projectName : ""}`,
          'The was an error displaying the calculation progress.'
        );
        return;
      }

      if (progressObjectFromServer.message == CalculationProgressModel.CANCEL_CALCULATION || progressObjectFromServer.calculationId == -3){
        return;
      }

      if (progressObjectFromServer.calculationType == "LOG_FILE_DATA"){
        this.handleLogFileDataReceived(progressObjectFromServer);
        return;
      }

      if (progressObjectFromServer.calculationType == "PROCESSING_TOOL"){
        this.handleImportProcessingTool(progressObjectFromServer);
        return;
      }

      if (progressObjectFromServer.calculationType == "IMPORT_PROJECT"){
        try {
          if (progressObjectFromServer.calculationId == CalculationProgressModel.ERROR_FLAG){
            this.modalService.showMesssageModal("Error","Error importing project. Please make sure the ZIP file is valid.");
            return;
          }
          if (progressObjectFromServer.storageError == null || progressObjectFromServer.storageError == ""){
            this.modalService.showMesssageModal("Import Project","Project imported successfully.");
            return;
          }
          await firstValueFrom(this.projectService.deleteProjectCalculations(progressObjectFromServer.calculationId));
          await firstValueFrom(this.projectService.deleteProject(progressObjectFromServer.calculationId));
          if (progressObjectFromServer.storageError == CalculationProgressModel.INSUFFICIENT_STORAGE_ON_BOTH){
            this.modalService.showMesssageModal("Error","Error importing project. Insufficient storage on terrains and calculations.");
            return;
          }
          if (progressObjectFromServer.storageError == CalculationProgressModel.INSUFFICIENT_STORAGE_ON_CALCS){
            this.modalService.showMesssageModal("Error","Error importing project. Insufficient storage on calculations.");
            return;
          }
          if (progressObjectFromServer.storageError == CalculationProgressModel.INSUFFICIENT_STORAGE_ON_TERRAINS){
            this.modalService.showMesssageModal("Error","Error importing project. Insufficient storage on terrains.");
            return;
          }
          if (progressObjectFromServer.storageError == CalculationProgressModel.ERROR_ON_PROJECT_IMPORT){
            this.modalService.showMesssageModal("Error","Error importing project. Error importing project. Please make sure the ZIP file is valid.");
            return;
          }
        } finally {
          this.projectService.getAllProjects();
          this.uiBlock.stopBlockWaitForProgress();
        }
      }

      if (progressObjectFromServer.calculationType == "TERRAIN_UPLOAD"){
        const terrainName = progressObjectFromServer.calculationName.match(/Importing Terrain: (.+)/)[1];
        if (progressObjectFromServer.calculationId == null || progressObjectFromServer.calculationId == CalculationProgressModel.ERROR_FLAG){
          this.modalService.showMesssageModal(
            'Save Terrain Error',
            `Error saving terrain: ${terrainName}, Project: ${progressObjectFromServer.projectName}. Please make sure you are importing the correct file.`
          );
          return;
        }

        this.configurationService.fetchTerrainsByProject(this.projectService.activeProject.id);
        this.modalService.showMesssageModal(
          'Terrain Upload',
          `Terrain uploaded successfully: ${terrainName}, Project: ${progressObjectFromServer.projectName}.`
        );
        return;
      }

      if (progressObjectFromServer.calculationType == "VALIDATE_TERRAIN"){
        const terrainName = progressObjectFromServer.calculationName.match(/Validating Terrain: (.+)/)[1];
        if (progressObjectFromServer.calculationId != CalculationProgressModel.VALID_FLAG){
          if (progressObjectFromServer.terrainCorruptedFiles == null){
            this.modalService.showMesssageModal(
              `Terrain files are corrupted - ${terrainName}, ${progressObjectFromServer.projectName}`,
              `Terrain: ${terrainName}, Project: ${progressObjectFromServer.projectName}, seems to be empty. No terrain files were found. Terrain deleted.`
            );
          } else {
            this.modalService.showMesssageModal(
              `Terrain files are corrupted - ${terrainName}, ${progressObjectFromServer.projectName}`,
              `The following files, belonging to '${terrainName}' terrain, Project: ${progressObjectFromServer.projectName}, are corrupted:
               ${progressObjectFromServer.terrainCorruptedFiles.join(",")}.` +
              ` Terrain deleted.`
            );
          }
          const terrain = await this.configurationService.fetchTerrainById(progressObjectFromServer.calculationId);
          this.configurationService.deleteTerrain(terrain.id, terrain.projectId);
          if (terrain.projectId != this.projectService.activeProject.id){
            return;
          }
          if (!this.configurationService.savedTerrains$){
            return;
          }
          let terrainInListIndex = this.configurationService.savedTerrains$.getValue().findIndex(t => t.id == progressObjectFromServer.calculationId);
          if (terrainInListIndex != -1){
            const updatedTerrains = this.configurationService.savedTerrains$.getValue();
            updatedTerrains.splice(terrainInListIndex, 1);
            for (terrainInListIndex; terrainInListIndex<updatedTerrains.length;terrainInListIndex++){
              updatedTerrains[terrainInListIndex].position--;
            }
          }
          return;

        }

        if (progressObjectFromServer.calculationId == CalculationProgressModel.VALID_FLAG){
          this.modalService.showMesssageModal(
            `Terrain files are valid - ${terrainName}, ${progressObjectFromServer.projectName}`,
            `Files belonging to '${terrainName}' terrain, Project: ${progressObjectFromServer.projectName}, are valid.`
          );
          return;
        }

      }
      if (progressObjectFromServer.calculationId != null && !this.previouslyFetchedCalculations.includes(progressObjectFromServer.calculationId)){
        if (progressObjectFromServer.calculationId == CalculationProgressModel.ERROR_FLAG){ // error on calculation
          this.modalService.showMesssageModal(
            `Calculation unsuccessful - ${progressObjectFromServer.calculationName}, ${progressObjectFromServer.projectName}`,
            `There was an error while performing the calculation: ${progressObjectFromServer.calculationName}. Project: ${progressObjectFromServer.projectName}.
             Please confirm all the data and try again.`
          );
          if (progressObjectFromServer.calculationType == "HORIZON_TOOL"){
            this.horizonToolLoading = false;
          }
          return;
        }

        const calculation: CalculationModel = await this.calculationsService.getCalculationById(progressObjectFromServer.calculationId);
        if (calculation != null && calculation.projectId == this.projectService.activeProject.id){
          if (calculation.results && calculation.results.length > 0){
            CalculationsService.decompressCalculationResult(calculation);
          }
          if (this.isTemporaryCalculation(calculation)){
            this.handleTemporaryCalculation(calculation);
            return;
          }
          if (calculation.calcType == 'TERRAIN_HEIGHT_BULK'){
            this.mapTerrainHeightToNextCalculation(calculation);
            return;
          }

          if(calculation.calcType == 'PROCEDURE_ANALYSIS'){
            const calc_info: CalcConfigInfo = await this.calculationsService.getCalcInfo(progressObjectFromServer.calculationId);
            let featureResults = [];
            calculation.results.forEach(resultItem => {
              featureResults.push(resultItem.result);
            });

            const performanceAnalysisDTO : PerformanceAnalysisDTO = JSON.parse(calc_info.calcConfig).performanceAnalysisDTO;
            const geoJsonFormat = new GeoJSON();
            const procedureFeature = geoJsonFormat.readFeature(performanceAnalysisDTO.procedureFeatureJson) as Feature<Geometry>;
            performanceAnalysisDTO.procedureModel = this.pointsService.transformFeatureInProcedure(procedureFeature);
            this.openNewPerformanceAnalysisTabFunction(performanceAnalysisDTO,featureResults,calculation.id);
            return;
          }
          this.calculationsService.addCalculationToMap(calculation, false);
          const calculationVisibilityExists = this.userSettingsService.userSettings.calculationsVisibility.findIndex(cv => cv.id == calculation.id) != -1;
          if (!calculationVisibilityExists){
            let index = this.calculationsService
                .getCalculationsLayer()
                .childLayers.findIndex((x) => x.calcID == calculation.id);
            if (index != -1){
              this.calculationsService.storeVisibilityCalculationInUserSettings(this.calculationsService.getCalculationsLayer().childLayers[index]);
            }
          }
          this.calculationsService.adjustCalculationVisibilityByUserSettings(calculation);
        }
        return;
      }
    }finally {
      this.progressMap.delete(progressGuid);
    }
  }


  handleImportProcessingTool(progressObjectFromServer: CalculationProgressModel){
    if (progressObjectFromServer.calculationId == CalculationProgressModel.ERROR_FLAG){
      this.modalService.showMesssageModal(
        'Error Importing Processing Tool',
        `There was an error import processing tool. Please make sure you are importing the correct file.`
      )
      this.deleteProcessingToolProgress();
      return;
    }

    const processingToolId = progressObjectFromServer.calculationId;
    const projectId = this.projectService.activeProject.id;

    this.http.post(
      this.endpointsService.endpointImportProcessingToolThirdStage + `/${processingToolId}`,
      {},
      {
        ...this.endpointsService.httpOptions,
        params: { projectId: projectId }
      }
    ).subscribe({
      next: () => {
        this.http.get(
          this.endpointsService.endpointGetProcessingTool + `/${processingToolId}`,
          this.endpointsService.httpOptions
        )
        .subscribe({
          next: (processingToolResponse: Processingtool) => {
            let processingTool :any = JSON.parse(processingToolResponse.processingtoolstr);
            this.pointsService.addProcessingToolToMap(processingTool.infoRows, processingToolResponse.layername, processingTool.fileFormat, processingTool.config, processingTool.defaultConfig, processingToolResponse.id);
            this.userSettingsService.createNewVisibilityLayer('Processing Tool',"" + processingToolResponse.id);
            this.deleteProcessingToolProgress();
          },
          error: (error) => {
            console.error(error);
            this.modalService.showMesssageModal(
              'Error Importing Processing Tool',
              `There was an error import processing tool. Please make sure you are importing the correct file.`
            )
            this.deleteProcessingToolProgress();
          }
        })
      },
      error: (error) => {
        console.error(error);
        this.modalService.showMesssageModal(
          'Error Importing Processing Tool',
          `There was an error finalizing the processing tool import.`
        )
        this.deleteProcessingToolProgress();
      }
    })
  }

  private deleteProcessingToolProgress(){
    for (const [id, progress] of this.progressMap.entries()){
      if (progress.calculationType == "PROCESSING_TOOL" && progress.loadedPercentage == 100){
        this.progressMap.delete(id);
      }
    }
  }

  handleLogFileDataReceived(progressObjectFromServer: CalculationProgressModel){
    if (progressObjectFromServer.calculationId != CalculationProgressModel.VALID_FLAG){
      this.modalService.showMesssageModal(
        'Error Downloading',
        'There was an error downloading the Error Logs'
      );
      return;
    }
    const blob = this.utilsService.base64ToBlob(progressObjectFromServer.logFilesData);

    fileSaver.saveAs(blob,ErrorLogsDisplayComponent.getPrettyCalculationName(progressObjectFromServer.calculationName) + " Error Logs.zip");
    this.modalService.showMesssageModal(
      'Download successfull',
      'The Error Logs were downloaded successfully.'
    );
  }

  handleTemporaryCalculation(calculation: CalculationModel, isFromFetchAll?: boolean){
    this.calculationsService.deleteCalculation(calculation.id, false);
    const data = JSON.parse(calculation.results[0].result);
    let geoJsonFormat: GeoJSON;
    let procedureFeature: Feature<Geometry>;
    switch (calculation.calcType){
      case "VERTICAL_PROFILE":
        geoJsonFormat = new GeoJSON();
        procedureFeature = geoJsonFormat.readFeature(data.calculationPerformanceAnalysisDTO.procedureFeatureJson) as Feature<Geometry>;
        data.calculationPerformanceAnalysisDTO.procedureModel = this.pointsService.transformFeatureInProcedure(procedureFeature);
        this.openNewVerticalProfileTabFunction(data.calculationPerformanceAnalysisDTO,data);
        break;
      case "ROUTE_PERFORMANCE":
        geoJsonFormat = new GeoJSON();
        procedureFeature = geoJsonFormat.readFeature(data.calculationPerformanceAnalysisDTO.procedureFeatureJson) as Feature<Geometry>;
        data.calculationPerformanceAnalysisDTO.procedureModel = this.pointsService.transformFeatureInProcedure(procedureFeature);
        this.openNewPerformanceReportTabFunction(data.calculationPerformanceAnalysisDTO,data);
        break;
      case "COVERAGE_QUANTITATIVE_ASSESSMENT":
        this.openNewCoveraveQuantitativeAssessmentTab(data);
        break;
      case "HORIZON_TOOL":
        isFromFetchAll? this.horizonToolLoading = false: this.performHorizonTool(data);
        break;
      default:
        break;
    }
  }

  async mapTerrainHeightToNextCalculation(calculation: CalculationModel, pendingCalculationMap?: PendingCalculationMap){
    try {
      let pendingCalculationDTO: PendingCalculationDTO = null;
      if (!pendingCalculationMap){
        // used when response comes normally, from progress.
        pendingCalculationDTO = await this.getPendingCalculationByCalculationId(calculation.id);
      } else {
        // used when initializeProgressService() is called. On app refresh, start, or project change.
        pendingCalculationDTO = JSON.parse(pendingCalculationMap.pendingCalculation);
      }

      if (pendingCalculationDTO == null){
        throw new Error("Error performing Calculation: Could not get Elevation");
      }

      const pendingCalculation: PendingCalculation = PendingCalculationDTO.mapToPendingCalculation(pendingCalculationDTO);
      const terrainInformation: any = JSON.parse(calculation.results[0].result);
      let navaidsEffectiveHeight: number[];


      //#region GRIT
      if(pendingCalculation.calculationType == PendingCalculationType.IMPACT_ASSESSMENT){
        let heights = terrainInformation.map(information => {
          return Math.round(information["terrainHeigth"]);
        })

        this.calculationsControlService.
        resumeImpactAssessmentCalculation
        (
          pendingCalculation.calculationDTO, heights
        )
        return;
      }
      //endregion

      if (pendingCalculation.selectedFeatures != null){ // depends on calculation type
        navaidsEffectiveHeight = this.pointsService.resumeGetNavaidFeatureElevationMetersBulk(terrainInformation, pendingCalculation.selectedFeatures);
      } else {
        navaidsEffectiveHeight = this.pointsService.resumeGetNavaidElevationMetersBulk(terrainInformation, pendingCalculation.selectedNavaids);
      }

      let navaidsTerrainHeight: number[];

      if (pendingCalculation.elevationType != "Relative") {
        navaidsTerrainHeight = navaidsEffectiveHeight;
        navaidsEffectiveHeight = this.getNavaidsPublishedElevationFromPendingCalculation(pendingCalculation);
      }

      switch (pendingCalculation.calculationType) { // resume calculations
        case PendingCalculationType.PERFORMANCE_ANALYSIS:
          pendingCalculation.calculationDTO.navaids = this.calculationsControlService.resumeGetNavaidDTOCalculationArray
            (
            pendingCalculation.selectedFeatures, navaidsEffectiveHeight
            );
          this.calculationValidationService.
            resumeOpenPerformanceAnalysisPopup
            (
              pendingCalculation.selectedFeatures, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.VERTICAL_PROFILE:
          pendingCalculation.calculationDTO.navaids = this.calculationsControlService.resumeGetNavaidDTOCalculationArray
            (
            pendingCalculation.selectedFeatures, navaidsEffectiveHeight
            );
          this.calculationValidationService.
            resumeOpenVerticalProfilePopup
            (
              pendingCalculation.selectedFeatures, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.PERFORMANCE_REPORT:
          pendingCalculation.calculationDTO.navaids = this.calculationsControlService.resumeGetNavaidDTOCalculationArray
            (
            pendingCalculation.selectedFeatures, navaidsEffectiveHeight
            );
          this.calculationValidationService.
          resumeOpenRoutePerformanceReportPopup
          (
            pendingCalculation.selectedFeatures, pendingCalculation.calculationDTO, navaidsTerrainHeight
          )
          break;
        case PendingCalculationType.COVERAGE_ASSESSMENT_TOOL:
          this.calculationsControlService.
            resumeCoverageAssessmentToolCalculation
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.modalDTO, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.COVERAGE:
          this.calculationsControlService.
            resumeCoverageCalculation
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.modalDTO, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.OVERLAP:
          this.calculationsControlService.
            resumeOverlapCalculation
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.CUMULATIVE_COVERAGE:
          this.calculationsControlService.
            resumeCumulativeCoverageCalculation
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.modalDTO, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.PERFORMANCE_DME:
          this.calculationsControlService.
            resumePerformanceCalculationDME
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.modalDTO, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.PERFORMANCE_VOR:
          this.calculationsControlService.
            resumePerformanceCalculationVOR
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.modalDTO, pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.SITING_TOOL:
          this.sitingToolService.
            resumeSitingToolArea
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight,
              pendingCalculation.calculationDTO, navaidsTerrainHeight
            )
          break;
        case PendingCalculationType.DOC_AND_SEC:
          this.docSectorizationDisplayService.
            resumeCalculateDocSectorizationDisplay
            (
              pendingCalculation.selectedFeatures, navaidsEffectiveHeight, navaidsTerrainHeight
            )
          break;
        default:
            throw new Error("Error performing Calculation: Could not get Elevation");
      }
    } catch (e){
      const project = this.projectService.projects.find(p => p.id == calculation.projectId);
      this.modalService.showMesssageModal(
        `Calculation unsuccessful - Terrain Height${project != null? ", Project: " + project.name : ""}`,
        `The was an error while performing the calculation: Terrain Height${project != null? ", Project: " + project.name : ""}. Please confirm all the data and try again.`
      );
      throw e;
    } finally {
      // delete terrain height calculation and pending calculation.
      const pendingCalculationDeleted: boolean = await this.deletePendingCalculationByCalculationId(calculation.id);
      if (!pendingCalculationDeleted){
        console.error("Error deleting pending calculation!")
      }
    }
  }


  getNavaidsPublishedElevationFromPendingCalculation(pendingCalculation: PendingCalculation): number[] {
    let navaidsPublishedHeight: number[] = [];
    if (pendingCalculation.selectedFeatures != null) { // depends on calculation type
      for (const navaidFeature of pendingCalculation.selectedFeatures) {
        navaidsPublishedHeight.push(this.utilsService.feetToMeters(navaidFeature.get('elevation')));
      }
    } else {
      for (const navaid of pendingCalculation.selectedNavaids) {
        navaidsPublishedHeight.push(+navaid.elevationMeters);
      }
    }
    return navaidsPublishedHeight;
  }


  async getPendingCalculationByCalculationId(calculationId: number): Promise<PendingCalculationDTO>{
    return await firstValueFrom(this.http.get<PendingCalculationDTO>(
      this.endpointsService.apiPendingCalculation + `/${calculationId}`,
      this.endpointsService.httpOptions
      ))
  }

  async getAllPendingCalculations(): Promise<PendingCalculationMap[]>{
    const pendingCalculations: PendingCalculationMap[] = await firstValueFrom(this.http.get<PendingCalculationMap[]>(
      this.endpointsService.getAllPendingCalculations + `/${this.projectService.activeProject.id}`,
      this.endpointsService.httpOptions
    ))

    this.previouslyFetchedCalculations.push(...pendingCalculations.map(p => p.calculationId));

    return pendingCalculations;
  }

  async deletePendingCalculationByCalculationId(calculationId: number){
    return await firstValueFrom(this.http.delete<boolean>(
    this.endpointsService.apiPendingCalculation + `/${calculationId}`,
    this.endpointsService.httpOptions
    ))
  }


  public getTerrainUploadOnProgress(): string{
    for (let [key, value] of this.progressMap) {
      if (value.calculationType === "TERRAIN_UPLOAD") {
        return key;
      }
    }
    return null;
  }

}

class RetriableError extends Error { }
class FatalError extends Error { }
